import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import {
  AuthResponse,
  BeginBusinessOnboardingRequest,
  BeginBusinessOnboardingResponse,
  CompleteBusinessAccountRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LogoutRequest,
  RefreshTokenRequest,
  RegisterBusinessRequest,
  RegisterBusinessResponse,
  ResetPasswordRequest,
} from '../models/auth.models';
import { User } from '../models/user.models';
import { ApiService } from '../services/api.service';
import { apiMessage, unwrapApiResponse } from '../utils/api-response';
import { CurrentUserService } from './current-user.service';
import { TokenService } from './token.service';
import { BusinessLocationContextService } from '../services/business-location-context.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly tokens = inject(TokenService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly locationContext = inject(BusinessLocationContextService);
  private sessionRefreshInFlight?: Observable<User>;
  private lastSessionRefreshAt = 0;

  login(payload: LoginRequest): Observable<AuthResponse> {
    return this.api
      .post<ApiResponse<AuthResponse> | AuthResponse, LoginRequest>(
        API_ENDPOINTS.auth.login,
        payload,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        tap((response) => this.applySession(response)),
      );
  }

  platformLogin(payload: LoginRequest): Observable<AuthResponse> {
    return this.api
      .post<ApiResponse<AuthResponse> | AuthResponse, LoginRequest>(
        API_ENDPOINTS.auth.platformLogin,
        payload,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        tap((response) => this.applySession(response)),
      );
  }

  registerBusiness(payload: RegisterBusinessRequest): Observable<RegisterBusinessResponse> {
    return this.api
      .post<ApiResponse<RegisterBusinessResponse> | RegisterBusinessResponse, RegisterBusinessRequest>(
        API_ENDPOINTS.tenants.register,
        payload,
        'identity',
        30_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  beginBusinessOnboarding(payload: BeginBusinessOnboardingRequest): Observable<BeginBusinessOnboardingResponse> {
    return this.api
      .post<ApiResponse<BeginBusinessOnboardingResponse> | BeginBusinessOnboardingResponse, BeginBusinessOnboardingRequest>(
        API_ENDPOINTS.tenants.beginOnboarding,
        payload,
        'identity',
        30_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  completeBusinessAccount(payload: CompleteBusinessAccountRequest): Observable<{ completed: boolean; email: string }> {
    return this.api
      .post<ApiResponse<{ completed: boolean; email: string }> | { completed: boolean; email: string }, CompleteBusinessAccountRequest>(
        API_ENDPOINTS.tenants.completeAccount,
        payload,
        'identity',
        30_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  forgotPassword(payload: ForgotPasswordRequest): Observable<{ message: string }> {
    return this.api
      .post<ApiResponse<unknown> | { message: string }, ForgotPasswordRequest>(
        API_ENDPOINTS.auth.forgotPassword,
        payload,
      )
      .pipe(
        map((response) => ({ message: apiMessage(response, 'Password reset instructions sent.') })),
      );
  }

  resetPassword(payload: ResetPasswordRequest): Observable<{ message: string }> {
    return this.api
      .post<ApiResponse<unknown> | { message: string }, ResetPasswordRequest>(
        API_ENDPOINTS.auth.resetPassword,
        payload,
      )
      .pipe(map((response) => ({ message: apiMessage(response, 'Password updated.') })));
  }

  changePassword(payload: { currentPassword?: string; newPassword: string; confirmPassword: string }): Observable<{ succeeded: boolean }> {
    return this.api
      .post<ApiResponse<{ succeeded: boolean }> | { succeeded: boolean }, typeof payload>(API_ENDPOINTS.auth.changePassword, payload)
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  loadCurrentUser(): Observable<User> {
    return this.api.get<ApiResponse<User> | User>(API_ENDPOINTS.auth.me).pipe(
      map((response) => unwrapApiResponse(response)),
      tap((user) => {
        this.currentUser.setUser(user);
      }),
    );
  }

  refreshToken(refreshToken: string): Observable<{ accessToken: string; refreshToken: string; expiresAt: string }> {
    return this.api
      .post<ApiResponse<{ accessToken: string; refreshToken: string; expiresAt: string }> | { accessToken: string; refreshToken: string; expiresAt: string }, RefreshTokenRequest>(
        API_ENDPOINTS.auth.refresh,
        {
          refreshToken,
          ipAddress: null,
          userAgent: navigator.userAgent,
        },
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        tap((response) => this.tokens.setTokens(response.accessToken, response.refreshToken)),
      );
  }

  /** Refreshes JWT claims and the in-memory user without interrupting the signed-in session. */
  refreshSession(): Observable<User> {
    const refreshToken = this.tokens.getRefreshToken();
    if (!refreshToken) return this.loadCurrentUser();
    // A still-valid access token must be allowed to rebuild the in-memory session
    // after a page refresh even when an older refresh token has been revoked.
    return this.refreshToken(refreshToken).pipe(
      switchMap(() => this.loadCurrentUser()),
      catchError(() => this.loadCurrentUser()),
    );
  }

  ensureFreshSession(maxAgeMs = 15_000): Observable<User | null> {
    const current = this.currentUser.user();
    if (current && Date.now() - this.lastSessionRefreshAt < maxAgeMs) return of(current);
    if (this.sessionRefreshInFlight) return this.sessionRefreshInFlight;
    this.sessionRefreshInFlight = this.refreshSession().pipe(
      tap(() => (this.lastSessionRefreshAt = Date.now())),
      finalize(() => (this.sessionRefreshInFlight = undefined)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.sessionRefreshInFlight;
  }

  logout(): void {
    const refreshToken = this.tokens.getRefreshToken();

    if (refreshToken) {
      this.api
        .post<void, LogoutRequest>(API_ENDPOINTS.auth.logout, { refreshToken })
        .subscribe({ error: () => undefined });
    }

    this.tokens.clear();
    this.currentUser.setUser(null);
    this.locationContext.clear();
    this.lastSessionRefreshAt = 0;
  }

  businessLandingRoute(): string {
    return this.currentUser.businessLandingRoute();
  }

  private applySession(response: AuthResponse): void {
    this.tokens.setTokens(response.accessToken, response.refreshToken);
    const roles = response.roles ?? response.user.roles ?? [];
    const permissions = response.permissions ?? response.user.permissions ?? [];
    const primaryRole = response.user.role ?? roles[0] ?? '';

    this.currentUser.setUser({
      ...response.user,
      name:
        response.user.name ??
        ([response.user.firstName, response.user.lastName].filter(Boolean).join(' ').trim() ||
          response.user.userName ||
          response.user.email),
      role: primaryRole,
      roles,
      permissions,
      status: response.user.status ?? 'Active',
      business: response.user.business ?? '',
    });
  }
}
